import { HStack, VStack } from '@chakra-ui/react';
import React, { useCallback } from 'react';

import { CheckConstraint } from './CheckConstraint';
import { CurrencyInput } from './CurrencyInput';
import { OptionalClassWrapper } from './Shared';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  useFundingRequestTemplateContextV1,
  RecycleRequest as RecycleRequestType,
} from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';

type RecycleRequestProps = {
  isDisabled?: boolean;
  recycleRequest: RecycleRequestType;
};

export const FxDetails = ({
  fxDetails: { currencyCode, exchangeRate },
}: {
  fxDetails: { currencyCode: String; exchangeRate: number };
}) => (
  <HStack spacing="24px">
    <Typography colorScheme="gray" variant="base-muted" fontWeight="normal">
      Exchange Rate: 1 {currencyCode} = {exchangeRate} USD
    </Typography>
  </HStack>
);

export const RecycleRequest = ({
  isDisabled = false,
  recycleRequest: {
    key,
    requestAmount,
    fxDetails,
    constraints: {
      minimum: minimumConstraint,
      maximum: maximumConstraint,
      increment: incrementConstraint,
    },
  },
}: RecycleRequestProps) => {
  const { context } = useFundingRequestTemplateContextV1();

  const updateState = useCallback(
    (newValue?: number) => {
      if (typeof newValue === 'number') {
        context.setValue({
          type: 'setRecycleRequests',
          options: {
            key,
            amount: newValue,
          },
        });
      }
    },
    [key, context],
  );

  return (
    <VStack w="100%" alignItems="flex-start">
      <CurrencyInput
        state={requestAmount}
        updateState={updateState}
        isDisabled={context.allowedActions === 'readOnly' || isDisabled}
      />
      <HStack alignSelf="flex-end" spacing="24px">
        {/* if collection account is in different currency render FxDetails */}
        {fxDetails && <FxDetails fxDetails={fxDetails} />}

        {/* @jendevelops constraints TODO */}
        <CheckConstraint
          type="minimum"
          value={requestAmount}
          constraintValue={minimumConstraint}
        />
        {incrementConstraint && (
          <CheckConstraint
            type="increment"
            value={requestAmount}
            constraintValue={incrementConstraint}
          />
        )}
        <CheckConstraint
          type="maximum"
          value={requestAmount}
          constraintValue={maximumConstraint}
        />
      </HStack>
    </VStack>
  );
};

export const RecycleRequests = ({
  isDisabled = false,
}: {
  isDisabled?: boolean;
}) => {
  const {
    context: {
      data: { recycleRequests, netRecycleRequest },
    },
  } = useFundingRequestTemplateContextV1();

  if (recycleRequests.length === 0) {
    return null;
  }

  const isClassed = recycleRequests.length > 1;

  return (
    <VStack spacing="24px" w="100%">
      {!!netRecycleRequest && (
        // netRecycleRequest will be available when there are multiple collection accounts. It represents the sum of all recycle request
        <OptionalClassWrapper
          isClassed={false}
          className={netRecycleRequest.name}
          key={netRecycleRequest.key}
        >
          {/* render disabled, since this can not be edited directly */}
          <RecycleRequest recycleRequest={netRecycleRequest} isDisabled />
        </OptionalClassWrapper>
      )}

      {recycleRequests.map((recycleRequest) => (
        <OptionalClassWrapper
          isClassed={isClassed}
          className={recycleRequest.name}
          key={recycleRequest.key}
        >
          <RecycleRequest
            recycleRequest={recycleRequest}
            isDisabled={isDisabled}
          />
        </OptionalClassWrapper>
      ))}
    </VStack>
  );
};
